import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import i18n, { applyDocumentLanguage } from '@/i18n';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Globe, Check } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'ha', name: 'Hausa', native: 'Hausa' },
  { code: 'yo', name: 'Yoruba', native: 'Yorùbá' },
  { code: 'ig', name: 'Igbo', native: 'Igbo' },
  { code: 'fr', name: 'French', native: 'Français' },
  { code: 'ar', name: 'Arabic', native: 'العربية' },
];

export default function LanguagePage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [selectedLang, setSelectedLang] = useState(user?.language || i18n.language || 'en');

  const handleSave = async () => {
    await i18n.changeLanguage(selectedLang);
    applyDocumentLanguage(selectedLang);
    if (user) {
      updateUser({ language: selectedLang });
    }
    const chosen = LANGUAGES.find((l) => l.code === selectedLang);
    toast({
      title: t('language.updated'),
      description: t('language.updatedDesc', { name: chosen?.native || chosen?.name }),
    });
    navigate(-1);
  };

  return (
    <div className="p-4 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl font-bold">{t('language.title')}</h1>
          <p className="text-muted-foreground text-sm">{t('language.subtitle')}</p>
        </div>
      </div>

      {/* Language Options */}
      <div className="space-y-3">
        {LANGUAGES.map((lang) => (
          <Card
            key={lang.code}
            className={`cursor-pointer transition-all ${selectedLang === lang.code ? 'border-farm-primary border-2' : ''}`}
            onClick={() => setSelectedLang(lang.code)}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <Globe className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-semibold">{lang.name}</p>
                  <p className="text-sm text-muted-foreground">{lang.native}</p>
                </div>
              </div>
              {selectedLang === lang.code && (
                <div className="w-6 h-6 bg-farm-primary rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Save Button */}
      <Button
        className="w-full mt-6 bg-farm-primary hover:bg-farm-primary-dark"
        onClick={handleSave}
      >
        {t('language.save')}
      </Button>
    </div>
  );
}
